'use client';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

/**
 * Inline "no account found" card — web parity with the native sign-in page's
 * unknown-account card. Rendered by the sign-in containers instead of
 * hard-bouncing an unknown identifier to sign-up: the user may have signed
 * up on the OTHER channel (or the typed credential holds a typo), so offer
 * trying the other method before pushing them into creating a duplicate
 * account. `onTryOtherMethod` is undefined when the tenant only offers one
 * method — the copy adapts accordingly.
 */
export function UnknownAccountCard({
  channel,
  onTryOtherMethod,
  onCreateAccount,
  onDismiss,
}: {
  channel: 'phone' | 'email';
  onTryOtherMethod?: () => void;
  onCreateAccount: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className="border-primary/30 bg-primary/5 rounded-lg border p-4 text-sm"
      data-test="signin-unknown-account-card"
    >
      <p className="text-foreground font-medium">
        {channel === 'phone' ? (
          <Trans
            i18nKey="auth:unknownPhoneHeading"
            defaults="No account found for this phone number"
          />
        ) : (
          <Trans
            i18nKey="auth:unknownEmailHeading"
            defaults="No account found for this email"
          />
        )}
      </p>

      <p className="text-muted-foreground mt-1">
        {onTryOtherMethod ? (
          <Trans
            i18nKey="auth:unknownAccountBody"
            defaults="You may have signed up with a different method — or you can create a new account."
          />
        ) : (
          <Trans
            i18nKey="auth:unknownAccountBodySingle"
            defaults="Check the details, or create a new account."
          />
        )}
      </p>

      <div className="mt-3 flex flex-col gap-2">
        {onTryOtherMethod ? (
          <Button
            type="button"
            onClick={onTryOtherMethod}
            data-test="unknown-account-try-other"
          >
            {channel === 'phone' ? (
              <Trans
                i18nKey="auth:tryEmailInstead"
                defaults="Try signing in with email"
              />
            ) : (
              <Trans
                i18nKey="auth:tryPhoneInstead"
                defaults="Try signing in with phone"
              />
            )}
          </Button>
        ) : null}

        <Button
          type="button"
          variant="outline"
          onClick={onCreateAccount}
          data-test="unknown-account-create"
        >
          <Trans i18nKey="auth:createNewAccount" defaults="Create an account" />
        </Button>

        <Button type="button" variant="ghost" onClick={onDismiss}>
          <Trans i18nKey="common:cancel" defaults="Cancel" />
        </Button>
      </div>
    </div>
  );
}
